import {
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  useUserContext,
  useHasPermission,
} from "@dexteel/mesf-core";

const moment = getMomentTz();
import { Refresh as RefreshIcon, Add as AddIcon } from "@mui/icons-material";
import { Typography, IconButton, Tooltip, Box, Button } from "@mui/material";
import { Group, Panel } from "react-resizable-panels";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { LazyBackDropLoading } from "@/controls/LazyLoading";
import {
  CasterTreePanel,
  MoldMapPanel,
  SetMoldOnlineModal,
  StrandMoldHistoryPanel,
} from "./components";
import copperMoldTubePng from "./components/map-view/assets/copper_mold_tube.png";
import levelControlPng from "./components/map-view/assets/level_control.png";
import oilConnectorPng from "./components/map-view/assets/oil_connector.png";
import moldBodyPng from "./components/map-view/assets/mold_body.png";
import waterJacketFlumePng from "./components/map-view/assets/water_jacket_flume.png";
import waterJacketFixingFlangePng from "./components/map-view/assets/water_jacket_fixing_flange.png";
import waterJacketSealPng from "./components/map-view/assets/water_jacket_seal.png";
import oilDistributionGasketPng from "./components/map-view/assets/oil_distribution_gasket.png";
import closingFlangePng from "./components/map-view/assets/closing_flange.png";
import upperFlangePng from "./components/map-view/assets/upper_flange.png";
import upperCollarPng from "./components/map-view/assets/upper_collar.png";
import lowUpperFlangePng from "./components/map-view/assets/low_upper_flange.png";
import upperCopperTubeSealPng from "./components/map-view/assets/upper_copper_tube_seal.png";
import upperSealPng from "./components/map-view/assets/upper_seal.png";
import lowerExternalSealPng from "./components/map-view/assets/lower_external_seal.png";
import lowerFlangeInternalSealPng from "./components/map-view/assets/lower_flange_internal_seal.png";
import fortRollFlangePng from "./components/map-view/assets/fort_roll_flange.png";
import lowerCopperTubeSealPng from "./components/map-view/assets/lower_copper_tube_seal.png";
import lowerCollarPng from "./components/map-view/assets/lower_collar.png";
import footRollPng from "./components/map-view/assets/foot_roll.png";
import rollShaftPng from "./components/map-view/assets/roll_shaft.png";
import rollMountingBracketPng from "./components/map-view/assets/roll_mounting_bracket.png";
import shaftKeeperPng from "./components/map-view/assets/shaft_keeper.png";
import moldRollSupportPng from "./components/map-view/assets/mold_roll_support.png";
import upperSprayNozzlePng from "./components/map-view/assets/upper_spray_nozzle.png";
import lowerSprayNozzlePng from "./components/map-view/assets/lower_spray_nozzle.png";
import {
  MetricsPanel,
  HistoryPanel,
  DetailsPanel,
  ViewToggle,
  NoPermissionModal,
  type ViewMode,
} from "../../shared/components";
import {
  InstallPartModal,
  EditPartModal,
  NewSparePartModal,
  NotesModal,
  SetInitialMetricModal,
  CorrectDatesModal,
  SetEquipmentStatusModal,
  DeleteConfirmModal,
} from "../../shared/components/modals";
import { useResizablePanels } from "../../../sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "../../../sy/components/ResizablePanelSeparator";
import { useVerticalDragResize } from "../../../sy/hooks/useVerticalDragResize";
import { VerticalDragSeparator } from "../../../sy/components/VerticalDragSeparator";
import { getPartDetailsParams } from "../../shared/utils/getPartDetailsParams";
import {
  usePartDetailsQuery,
  useEnsurePartDetailsData,
  type GetPartDetailsFn,
} from "../../shared/hooks/queries";
import {
  getPartDetails as getPartDetailsCaster,
  getStrandMoldHistory,
  getTaskMetadata,
  addTaskMetadata,
} from "./repositories/CasterMoldRepository";
import type { PartInfo } from "../../shared/models";
import type {
  PartDetailsMasterAPI,
  PartDetailsHistoryAPI,
  CasterTreeRow,
} from "./models";
import {
  PART_IDS,
  PART_TYPE_IDS,
  STRAND_CONTAINER_IDS,
  isSpareContainer,
  isStrandContainer,
  transformAPIToTreeData,
  getStrandName,
  transformHistoryAPIToEntries,
} from "./models";
import {
  useCasterMoldPartsTreeQuery,
  useRealtimeInvalidation,
} from "./hooks/queries";
import {
  useSetMoldOnlineMutation,
  useSetMoldOfflineMutation,
  useInstallMoldPartMutation,
  useInstallExistingPartMutation,
  useUninstallMoldPartMutation,
  useRepairMoldPartMutation,
  useUpsertMoldPartDetailsMutation,
  useCreateSparePartMutation,
  useDeleteSparePartMutation,
} from "./hooks/mutations";
import {
  useSetInitialMetricValueMutation,
  useUpdateTaskTimestampMutation,
} from "../../shared/hooks/mutations";

// Cast the caster-specific getPartDetails to the shared generic type.
// The runtime shapes are compatible — this cast bridges the typed rows vs Record<string, unknown>.
const getPartDetails = getPartDetailsCaster as unknown as GetPartDetailsFn;

/** Extract error message from an unknown error (TanStack Query default error type). */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};

export const CasterMoldPage = () => {
  const { state: userState } = useUserContext();
  const hasPermission = useHasPermission();

  // Permission control (same pattern as Roof/Sidewall)
  const canSetOnline = hasPermission("RBD.EQPCasterSetOnline");
  const canSetOffline = hasPermission("RBD.EQPCasterSetOffline");
  const canEditTimestamp = hasPermission("EQP.UpsertTask");

  // Resizable panels (horizontal — tree vs details)
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "caster-mold-panels",
      defaultLayout: { tree: 50, details: 50 },
    });

  // Vertical drag-to-resize for details+metrics vs history (content-adaptive)
  const { dragHeight, containerRef, handleDragStart, resetDragHeight } =
    useVerticalDragResize();

  // ==========================================================================
  // Query Hooks (replace loadData, loadHistory, loadMetrics)
  // ==========================================================================
  useRealtimeInvalidation();

  const {
    data: treeData = [],
    isLoading: isLoadingTree,
    refetch: refetchTree,
  } = useCasterMoldPartsTreeQuery();

  // Keep ref in sync for callbacks that need current data
  const treeDataRef = useRef<CasterTreeRow[]>([]);
  useEffect(() => {
    treeDataRef.current = treeData;
  }, [treeData]);

  // ==========================================================================
  // Mutation Hooks
  // ==========================================================================
  const setOnlineMutation = useSetMoldOnlineMutation();
  const setOfflineMutation = useSetMoldOfflineMutation();
  const installPartMutation = useInstallMoldPartMutation();
  const installExistingMutation = useInstallExistingPartMutation();
  const uninstallPartMutation = useUninstallMoldPartMutation();
  const repairPartMutation = useRepairMoldPartMutation();
  const upsertDetailsMutation = useUpsertMoldPartDetailsMutation();
  const createSpareMutation = useCreateSparePartMutation();
  const deleteSpareMutation = useDeleteSparePartMutation();
  const setInitialMetricMutation = useSetInitialMetricValueMutation([
    ["caster-mold-strand"],
    ["caster-mold-offline"],
    ["caster-mold-strand-history"],
    ["caster-mold-details"],
  ]);
  const updateTimestampMutation = useUpdateTaskTimestampMutation(
    [
      ["caster-mold-strand"],
      ["caster-mold-offline"],
      ["caster-mold-strand-history"],
      ["caster-mold-details"],
    ],
    "[RBD].[EQPUpdateTaskTimestampCast]",
  );

  const isMutating =
    setOnlineMutation.isPending ||
    setOfflineMutation.isPending ||
    installPartMutation.isPending ||
    installExistingMutation.isPending ||
    uninstallPartMutation.isPending ||
    repairPartMutation.isPending ||
    upsertDetailsMutation.isPending ||
    createSpareMutation.isPending ||
    deleteSpareMutation.isPending;

  // ==========================================================================
  // UI State
  // ==========================================================================
  const [selectedRow, setSelectedRow] = useState<CasterTreeRow | null>(null);
  /** Strand ID for the right panel (strand mold history). Separate from selectedRow so the tree can highlight the mold while the right panel shows strand history. */
  const [selectedStrandId, setSelectedStrandId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("tree");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Modal state for context menu actions
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [newSparePartModalOpen, setNewSparePartModalOpen] = useState(false);
  const [editPartModalOpen, setEditPartModalOpen] = useState(false);
  const [setInitialMetricModalOpen, setSetInitialMetricModalOpen] =
    useState(false);
  const [correctDatesModalOpen, setCorrectDatesModalOpen] = useState(false);
  const [correctDatesPartId, setCorrectDatesPartId] = useState<number | null>(
    null,
  );
  const [correctDatesPartName, setCorrectDatesPartName] = useState<
    string | null
  >(null);
  const [modalAction, setModalAction] = useState<"uninstall" | "repair" | null>(
    null,
  );
  const [actionRow, setActionRow] = useState<CasterTreeRow | null>(null);

  // Set Mold Online Modal state (strand-based)
  const [moldOnlineModalOpen, setMoldOnlineModalOpen] = useState(false);
  const [strandLocationTimestamp, setStrandLocationTimestamp] =
    useState<Date | null>(null);

  // Set Offline Modal state (uses shared SetEquipmentStatusModal)
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  // Delete Confirm Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // No Permission Modal state
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [noPermissionAction, setNoPermissionAction] = useState<string | null>(
    null,
  );

  // ==========================================================================
  // Derived Data from Query Cache (for modals that need master/metrics data)
  // ==========================================================================
  const partDetailsParams = selectedRow
    ? getPartDetailsParams(selectedRow)
    : null;

  // Page-level query for master data (deduped with MetricsPanel/HistoryPanel)
  const partDetailsQuery = usePartDetailsQuery(
    "caster-mold",
    partDetailsParams,
    getPartDetails,
  );

  const ensurePartDetailsData = useEnsurePartDetailsData(
    "caster-mold",
    getPartDetailsCaster as unknown as GetPartDetailsFn,
  );

  // Extract master data from the query result (for MetricsPanel uninstalledOn)
  const selectedEquipmentMasterData: PartDetailsMasterAPI | null =
    selectedRow?.rowType === "equipment" && partDetailsQuery.data
      ? ((partDetailsQuery.data.tables[0]
          ?.rows[0] as unknown as PartDetailsMasterAPI) ?? null)
      : null;

  // Extract metrics from the query result (for SetInitialMetricModal)
  const metrics = (partDetailsQuery.data?.tables[1]?.rows ?? []) as unknown as {
    MetricTypeId: number;
    PartialValue: number | null;
  }[];

  // ==========================================================================
  // Sync selected row with tree data after mutations/refetches
  // ==========================================================================
  useEffect(() => {
    if (treeData.length === 0) return;

    setSelectedRow((current) => {
      if (current) {
        // Keep selection in sync with updated tree data
        return treeData.find((row) => row.id === current.id) ?? null;
      }
      // Initial load: pre-select the online mold
      const onlineMold = treeData.find(
        (row) => row.rowType === "equipment" && row.Status === "Online",
      );
      return onlineMold ?? null;
    });
  }, [treeData]);

  // Reset vertical drag height when selection changes (re-adapt to content)
  useEffect(() => {
    resetDragHeight();
  }, [selectedRow, resetDragHeight]);

  // ============================================
  // Handlers
  // ============================================

  const handleRowSelection = (row: CasterTreeRow | null) => {
    // Strand container selected (tree view) → always show strand mold history
    if (row?.rowType === "container" && isStrandContainer(row.ContainerId)) {
      setSelectedStrandId(row.ContainerId!);
      setSelectedRow(row);
      return;
    }
    // Any other selection clears strand mode
    setSelectedStrandId(null);
    setSelectedRow(row);
  };

  /** Strand card clicked in map view.
   *  - Occupied strand → selectedRow = mold equipment row (tree highlights mold, right panel shows mold info)
   *  - Empty strand → selectedRow = strand container row (tree highlights strand) */
  const handleStrandSelected = (
    strandId: number,
    moldRow: CasterTreeRow | null,
  ) => {
    if (moldRow) {
      setSelectedStrandId(null);
      setSelectedRow(moldRow);
    } else {
      // Find the strand container row to highlight it in the tree
      const strandRow = treeDataRef.current.find(
        (r) =>
          r.rowType === "container" &&
          r.ContainerId === strandId &&
          isStrandContainer(r.ContainerId),
      );
      setSelectedStrandId(strandId);
      setSelectedRow(strandRow ?? null);
    }
  };

  // When switching to map view with a strand selected (from tree), resolve to the
  // installed mold so the right panel shows mold info instead of strand history.
  // Empty strands keep showing StrandMoldHistoryPanel.
  useEffect(() => {
    if (viewMode === "map" && selectedStrandId !== null) {
      const moldRow = treeDataRef.current.find(
        (r) =>
          r.rowType === "equipment" &&
          r.StrandPartId === selectedStrandId &&
          r.Status === "Online",
      );
      if (moldRow) {
        setSelectedStrandId(null);
        setSelectedRow(moldRow);
      }
    }
  }, [viewMode, selectedStrandId]);

  // Show no permission modal
  const showNoPermissionModal = (actionName: string) => {
    setNoPermissionAction(actionName);
    setNoPermissionModalOpen(true);
  };

  // ============================================
  // Status (Online/Offline) Handlers
  // ============================================

  const handleSetOnlineAction = async (row: CasterTreeRow) => {
    if (!canSetOnline) {
      showNoPermissionModal("set mold online");
      return;
    }
    // Fetch the strand's last mold task (uninstall date) for location constraint
    const strandId = isStrandContainer(row.ContainerId)
      ? row.ContainerId
      : undefined;
    if (strandId && !row.HasMoldInstalled) {
      try {
        const resp = await getStrandMoldHistory(strandId);
        const historyRows = resp?.data?.tables?.[2]?.rows ?? [];
        if (historyRows.length > 0) {
          const firstRow = historyRows[0] as unknown as Record<string, unknown>;
          setStrandLocationTimestamp(
            firstRow.Timestamp
              ? moment.utc(firstRow.Timestamp as string).toDate()
              : null,
          );
        } else {
          setStrandLocationTimestamp(null);
        }
      } catch {
        setStrandLocationTimestamp(null);
      }
    } else {
      setStrandLocationTimestamp(null);
    }
    setActionRow(row);
    setMoldOnlineModalOpen(true);
  };

  const handleSetOfflineAction = (row: CasterTreeRow) => {
    if (!canSetOffline) {
      showNoPermissionModal("set mold offline");
      return;
    }
    setActionRow(row);
    setStatusModalOpen(true);
  };

  /**
   * Handler for the Set Mold Online modal (strand-based).
   * Called with the resolved strandPartId and moldPartId regardless of which mode
   * the modal was opened in (fromStrand or fromSpares).
   */
  const handleSetMoldOnlineConfirm = async (
    strandPartId: number,
    moldPartId: number,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    try {
      // If strand already has a mold, SetOffline the occupant first (swap)
      const occupant = treeDataRef.current.find(
        (row) =>
          row.rowType === "equipment" &&
          row.StrandPartId === strandPartId &&
          row.Status === "Online" &&
          row.EquipmentId !== moldPartId,
      );

      let onlineTimestamp = timestamp;

      if (occupant) {
        await setOfflineMutation.mutateAsync({
          moldPartId: occupant.EquipmentId,
          timestamp,
        });

        /*
        CASE: Swap is produced
        Change Online's timestamp 1000ms after the offline to avoid collision between molds
        */
        if (timestamp) {
          onlineTimestamp = new Date(timestamp.getTime() + 1000);
        }
        /* Future improvements: 
        refactor this module to use useSetEquipmentOnlineOffline hook like Roof and Sidewall currently do
        Or change the modified timestamp logic upon a switch to the backend and avoid changing it from the UI */
      }

      await setOnlineMutation.mutateAsync({
        moldPartId,
        strandPartId,
        timestamp: onlineTimestamp,
      });
      setNotificationMessage(
        `Mold is now Online in ${getStrandName(treeDataRef.current, strandPartId)}`,
      );
      setMoldOnlineModalOpen(false);
      setActionRow(null);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  /**
   * Handler for the Set Offline confirmation modal.
   */
  const handleStatusModalConfirm = async (
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!actionRow?.EquipmentId) return;

    try {
      await setOfflineMutation.mutateAsync({
        moldPartId: actionRow.EquipmentId,
        timestamp,
      });
      setNotificationMessage(`${actionRow?.EquipmentName} is now Offline`);
      setStatusModalOpen(false);
      setActionRow(null);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  // ============================================
  // Part Action Handlers
  // ============================================

  const handleInstallPart = (
    equipmentId: number,
    positionId: number,
    partTypeId: number,
    partInfo: PartInfo,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installPartMutation.mutate(
      {
        moldPartId: equipmentId,
        positionId,
        partTypeId,
        partInfo,
        comments: notes,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleInstallExistingPart = (
    existingPartId: number,
    moldPartId: number,
    positionId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installExistingMutation.mutate(
      {
        existingPartId,
        moldPartId,
        positionId,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // ============================================
  // Context Menu Action Handlers
  // ============================================

  const handleInstallAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setInstallModalOpen(true);
  };

  const handleUninstallAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("uninstall");
    setNotesModalOpen(true);
  };

  const handleRepairAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("repair");
    setNotesModalOpen(true);
  };

  const handleNewSparePartAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setNewSparePartModalOpen(true);
  };

  const handleEditPartAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setEditPartModalOpen(true);
  };

  const handleSetInitialMetricAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setSetInitialMetricModalOpen(true);
  };

  const handleDeleteSparePartAction = (row: CasterTreeRow) => {
    setActionRow(row);
    setDeleteModalOpen(true);
  };

  // ============================================
  // Modal Confirm Handlers
  // ============================================

  const handleNotesModalConfirm = (
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!actionRow || !modalAction) return;

    const mutationCallbacks = {
      onSuccess: () => {
        const message =
          modalAction === "uninstall"
            ? "Part uninstalled successfully"
            : "Repair logged successfully";
        setNotificationMessage(message);
        setNotesModalOpen(false);
        setModalAction(null);
        setActionRow(null);
      },
      onError: (error: unknown) => {
        setErrorMessage(getErrorMessage(error));
      },
    };

    if (modalAction === "uninstall" && actionRow.L2PartId != null) {
      uninstallPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    } else if (modalAction === "repair" && actionRow.L2PartId != null) {
      repairPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    }
  };

  const handleNewSparePartConfirm = (partInfo: PartInfo) => {
    if (!actionRow) return;

    const containerId = actionRow.ContainerId ?? actionRow.EquipmentId;

    createSpareMutation.mutate(
      { containerId, partInfo, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part created successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleEditPartConfirm = (
    l2PartId: number,
    partInfo: PartInfo,
    _operatorNumber: number,
  ) => {
    upsertDetailsMutation.mutate(
      { partId: l2PartId, partInfo, comments: null },
      {
        onSuccess: () => {
          setNotificationMessage("Part updated successfully");
          setEditPartModalOpen(false);
          setActionRow(null);
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleUpdatePartDetails = (
    partId: number,
    partInfo: Partial<PartInfo>,
  ) => {
    upsertDetailsMutation.mutate(
      {
        partId,
        partInfo: {
          SerialNumber: partInfo.SerialNumber ?? "",
          WarehouseNumber: partInfo.WarehouseNumber ?? undefined,
          DrawingNumber: partInfo.DrawingNumber ?? undefined,
        },
        comments: null,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part details updated");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleSetInitialMetricConfirm = (
    newPartialValue: number,
    _operatorNumber: number,
    applyToChildren: boolean,
  ) => {
    const partId = applyToChildren
      ? (actionRow?.L2PartId ?? actionRow?.EquipmentId)
      : (actionRow?.L2PartId ?? selectedRow?.L2PartId);
    if (!partId) return;

    setInitialMetricMutation.mutate(
      {
        partId,
        newValue: newPartialValue,
        applyToChildren,
        userId: userState.userId,
      },
      {
        onSuccess: (resp: {
          data?: { tables?: { rows?: Record<string, unknown>[] }[] };
        }) => {
          if (applyToChildren) {
            const firstRow = resp.data?.tables?.[0]?.rows?.[0];
            const count =
              (firstRow as { AffectedCount?: number } | undefined)
                ?.AffectedCount ?? 0;
            setNotificationMessage(`Initial metric set for ${count} parts`);
          } else {
            setNotificationMessage("Initial metric set successfully");
          }
          setSetInitialMetricModalOpen(false);
          setActionRow(null);
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteSparePartConfirm = () => {
    if (!actionRow?.L2PartId) return;

    const deletedPartId = actionRow.L2PartId;
    deleteSpareMutation.mutate(
      { partId: deletedPartId, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part deleted successfully");
          setDeleteModalOpen(false);
          // Clear selection if deleted row was selected
          if (selectedRow?.L2PartId === deletedPartId) {
            setSelectedRow(null);
          }
          setActionRow(null);
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // ============================================
  // Correct Dates Handlers
  // ============================================

  const handleCorrectDatesAction = (row: CasterTreeRow) => {
    if (!canEditTimestamp) {
      showNoPermissionModal("correct dates");
      return;
    }
    const partId = row.rowType === "equipment" ? row.EquipmentId : row.L2PartId;
    if (!partId) return;
    setCorrectDatesPartId(partId);
    setCorrectDatesPartName(
      row.rowType === "equipment"
        ? (row.EquipmentName ?? null)
        : (row.PositionName ?? row.L2PartName ?? null),
    );
    setCorrectDatesModalOpen(true);
  };

  const handleCorrectDatesSave = async (
    taskId: number,
    newTimestamp: Date,
  ): Promise<boolean> => {
    try {
      await updateTimestampMutation.mutateAsync({
        taskId,
        newTimestamp,
      });
      setNotificationMessage("Timestamp updated successfully");
      return true;
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : "Failed to update timestamp";
      if (msg.includes("CASTER_EQP_INITIAL_DATETIME")) {
        setErrorMessage(
          "Cannot set date before the Caster EQP initialization date. This is the earliest allowed date for Caster equipment.",
        );
      } else {
        setErrorMessage(msg);
      }
      return false;
    }
  };

  const handleCloseCorrectDatesModal = () => {
    setCorrectDatesModalOpen(false);
    setCorrectDatesPartId(null);
    setCorrectDatesPartName(null);
  };

  // ============================================
  // Map View Handlers (3D view online/offline via MoldSelector)
  // ============================================

  const handleSetOfflineFromMap = async (moldPartId: number) => {
    const row = treeDataRef.current.find(
      (r) => r.EquipmentId === moldPartId && r.rowType === "equipment",
    );
    if (row) {
      handleSetOfflineAction(row);
    }
  };

  /** Install mold to an empty strand from the map view's strand selector */
  const handleInstallMoldToStrandFromMap = (strandPartId: number) => {
    const strandRow = treeDataRef.current.find(
      (r) => r.rowType === "container" && r.ContainerId === strandPartId,
    );
    if (strandRow) {
      handleSetOnlineAction(strandRow);
    }
  };

  // ============================================
  // Mold SVG Thumbnail Renderer (for DetailsPanel)
  // ============================================

  const renderMoldThumbnail = useCallback((row: CasterTreeRow): ReactNode => {
    if (!row) return null;

    // Piece-specific PNG thumbnails
    // Key format: top-level → "{positionId}", sub-positions → "{parentGroupPartTypeId}-{positionId}"
    const positionId = row.PositionId ?? null;
    const thumbnailKey = row.ParentGroupPartTypeId
      ? `${row.ParentGroupPartTypeId}-${positionId}`
      : `${positionId}`;
    const pieceThumbnails: Record<string, { src: string; alt: string }> = {
      "1": { src: copperMoldTubePng, alt: "Copper Mold Tube" },
      "2": { src: moldBodyPng, alt: "Mold Body" },
      "3": { src: oilDistributionGasketPng, alt: "Oil Distribution Gasket" },
      "4": { src: levelControlPng, alt: "Level Control" },
      "5": { src: oilConnectorPng, alt: "Oil Connector" },
      "6": { src: waterJacketFlumePng, alt: "Water Jacket Flume" },
      "1010-1": { src: upperFlangePng, alt: "Upper Flange" },
      "1010-2": { src: lowUpperFlangePng, alt: "Low Upper Flange" },
      "1010-3": { src: fortRollFlangePng, alt: "Lower Flange" },
      "1010-4": { src: closingFlangePng, alt: "Closing Flange" },
      "1010-5": {
        src: waterJacketFixingFlangePng,
        alt: "Water Jacket Fixing Flange",
      },
      "1011-1": { src: upperCollarPng, alt: "Upper Collar" },
      "1011-2": { src: lowerCollarPng, alt: "Lower Collar" },
      "1012-1": { src: upperSealPng, alt: "Upper Seal" },
      "1012-2": {
        src: upperCopperTubeSealPng,
        alt: "Copper Tube Seal Upper",
      },
      "1012-3": {
        src: lowerCopperTubeSealPng,
        alt: "Copper Tube Seal Lower",
      },
      "1012-4": { src: waterJacketSealPng, alt: "Water Jacket Seal" },
      "1012-5": {
        src: lowerFlangeInternalSealPng,
        alt: "Lower Internal Seal",
      },
      "1012-6": { src: lowerExternalSealPng, alt: "Lower External Seal" },
      "1012-7": { src: oilConnectorPng, alt: "Oil Connector O-Ring Upper" },
      "1012-8": { src: oilConnectorPng, alt: "Oil Connector O-Ring Lower" },
      "1013-1": { src: footRollPng, alt: "Foot Roll" },
      "1013-2": { src: rollShaftPng, alt: "Roll Shaft" },
      "1013-3": { src: rollMountingBracketPng, alt: "Roller Support" },
      "1013-4": { src: shaftKeeperPng, alt: "Shaft Keeper" },
      "1013-5": { src: moldRollSupportPng, alt: "Z1 Spray" },
      "1013-6": { src: upperSprayNozzlePng, alt: "Spray Nozzle Upper" },
      "1013-7": { src: lowerSprayNozzlePng, alt: "Spray Nozzle Lower" },
    };
    const pieceThumbnail = pieceThumbnails[thumbnailKey] ?? null;
    if (row.rowType !== "equipment" && !row.HasChildren && pieceThumbnail) {
      return (
        <img
          src={pieceThumbnail.src}
          alt={pieceThumbnail.alt}
          style={{ width: 180, height: "auto", objectFit: "contain" }}
        />
      );
    }

    return null;
  }, []);

  // ============================================
  // Modal Close Handlers
  // ============================================

  const handleCloseInstallModal = () => {
    setInstallModalOpen(false);
    setActionRow(null);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setModalAction(null);
    setActionRow(null);
  };

  const handleCloseNewSparePartModal = () => {
    setNewSparePartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseEditPartModal = () => {
    setEditPartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseSetInitialMetricModal = () => {
    setSetInitialMetricModalOpen(false);
    setActionRow(null);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setActionRow(null);
  };

  const handleCloseMoldOnlineModal = () => {
    setMoldOnlineModalOpen(false);
    setActionRow(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setActionRow(null);
  };

  // ============================================
  // Computed Props for SetMoldOnlineModal
  // ============================================

  /** Whether the "Set Online" action was triggered from a strand container */
  const moldOnlineFromStrand =
    actionRow != null && isStrandContainer(actionRow.ContainerId);

  /** Available molds in Mold Spares (for "fromStrand" mode) */
  const availableSpareMolds = moldOnlineModalOpen
    ? treeDataRef.current
        .filter(
          (row) =>
            row.rowType === "equipment" &&
            row.PartTypeId === PART_TYPE_IDS.MOLD &&
            row.Status === "Offline",
        )
        .map((row) => ({
          partId: row.EquipmentId,
          name: row.EquipmentName ?? `Mold #${row.EquipmentId}`,
          lastTaskTimestamp: row.InstalledDate ?? null,
        }))
    : [];

  /** Available strands for "fromSpares" mode — includes ALL strands.
   *  Occupied strands show the current mold name so the user knows they're swapping.
   *  The handler auto-offlines the occupant before installing the new mold. */
  const availableStrands = moldOnlineModalOpen
    ? STRAND_CONTAINER_IDS.map((strandId) => {
        const strandRow = treeDataRef.current.find(
          (row) => row.rowType === "container" && row.ContainerId === strandId,
        );
        const occupant = strandRow?.HasMoldInstalled
          ? treeDataRef.current.find(
              (row) =>
                row.rowType === "equipment" &&
                row.StrandPartId === strandId &&
                row.Status === "Online",
            )
          : null;
        const baseName = getStrandName(treeDataRef.current, strandId);
        const displayName = occupant
          ? `${baseName} (${occupant.EquipmentName ?? "occupied"})`
          : baseName;
        return {
          partId: strandId,
          name: displayName,
          occupantPartId: occupant?.EquipmentId,
          // Pre-computed from the offline mold being installed (fromSpares mode)
          lastTaskTimestamp: actionRow?.InstalledDate ?? null,
          // Last task on this strand (when previous mold was uninstalled)
          locationLastTimestamp: strandRow?.InstalledDate ?? null,
        };
      })
    : [];

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <HelmetDexteel title="Caster Mold" />
      <LazyBackDropLoading open={isLoadingTree || isMutating} />

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Caster Mold Tracking
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetchTree()}
                disabled={isLoadingTree}
                color="primary"
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Group
          id="caster-mold-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 150px)" }}
        >
          {/* Left panel: Tree View or Map View */}
          <Panel id="tree" minSize={20}>
            {viewMode === "tree" ? (
              <CasterTreePanel
                treeData={treeData}
                selectedRow={selectedRow}
                isLoading={isLoadingTree}
                onRowSelect={handleRowSelection}
                onSetOnline={handleSetOnlineAction}
                onSetOffline={handleSetOfflineAction}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                onCorrectDates={handleCorrectDatesAction}
                onDeleteSparePart={handleDeleteSparePartAction}
              />
            ) : (
              <MoldMapPanel
                treeData={treeData}
                selectedRow={selectedRow}
                onSelectRow={handleRowSelection}
                onSelectStrand={handleStrandSelected}
                onSetOffline={handleSetOfflineFromMap}
                onInstallMoldToStrand={handleInstallMoldToStrandFromMap}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                isLoading={isLoadingTree}
              />
            )}
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          {/* Right panel: Details + Metrics + History */}
          <Panel id="details" minSize={25}>
            {/* Strand selected → show strand mold history (driven by selectedStrandId, independent of selectedRow) */}
            {selectedStrandId !== null ? (
              <StrandMoldHistoryPanel
                strandId={selectedStrandId}
                strandName={getStrandName(treeData, selectedStrandId)}
              />
            ) : viewMode === "map" &&
              (selectedRow?.rowType === "container" ||
                selectedRow?.rowType === "sparesContainer") ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Part
                </Typography>
              </Box>
            ) : selectedRow?.rowType === "container" ||
              selectedRow?.rowType === "sparesContainer" ? (
              isSpareContainer(selectedRow?.ContainerId) &&
              selectedRow?.ContainerId !== PART_IDS.MOLD_SPARES ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: "4px",
                    backgroundColor: "background.paper",
                    gap: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Select a part or create a new one
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleNewSparePartAction(selectedRow)}
                  >
                    New Spare Part
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: "4px",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Please select a Part
                  </Typography>
                </Box>
              )
            ) : selectedRow?.rowType === "location" &&
              selectedRow?.HasChildren === true ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Child Part
                </Typography>
              </Box>
            ) : (
              <Box
                ref={containerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    ...(dragHeight != null
                      ? { height: dragHeight, overflow: "auto" }
                      : {}),
                  }}
                >
                  <DetailsPanel
                    treeData={treeData}
                    selectedRow={selectedRow}
                    onInstall={handleInstallAction}
                    onUninstall={handleUninstallAction}
                    onRepair={handleRepairAction}
                    onNewSparePart={handleNewSparePartAction}
                    isSpareContainer={isSpareContainer}
                    renderThumbnail={renderMoldThumbnail}
                    showMakeModelVendor={false}
                    showSerialNumber={
                      selectedRow?.PartTypeId === PART_TYPE_IDS.COPPER_MOLD_TUBE
                    }
                    showWarehouseDrawing
                    onUpdatePartDetails={handleUpdatePartDetails}
                  />
                  <MetricsPanel
                    section="caster-mold"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    selectedRow={selectedRow}
                    uninstalledOn={
                      selectedEquipmentMasterData?.UninstalledOn
                        ? new Date(selectedEquipmentMasterData.UninstalledOn)
                        : null
                    }
                    isSpareContainer={isSpareContainer}
                  />
                </Box>

                <VerticalDragSeparator
                  onMouseDown={handleDragStart}
                  onDoubleClick={resetDragHeight}
                />

                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <HistoryPanel
                    section="caster-mold"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    getTaskMetadataFn={getTaskMetadata}
                    transformHistory={(rows) =>
                      transformHistoryAPIToEntries(
                        rows as unknown as PartDetailsHistoryAPI[],
                      )
                    }
                    selectedRow={selectedRow}
                    isSpareContainer={isSpareContainer}
                    partInfoHeader="Part Info (SN | WN | DW)"
                  />
                </Box>
              </Box>
            )}
          </Panel>
        </Group>
      </Box>

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />

      {/* Install Part Modal */}
      <InstallPartModal
        open={installModalOpen}
        selectedRow={actionRow}
        treeData={treeData}
        onClose={handleCloseInstallModal}
        onInstallNewPart={handleInstallPart}
        onInstallExistingPart={handleInstallExistingPart}
        showMakeModelVendor={false}
        showSerialNumber={
          actionRow?.PartTypeId === PART_TYPE_IDS.COPPER_MOLD_TUBE
        }
        showWarehouseDrawing
        validateAgainstInitialDatetime
      />

      {/* Notes Modal for Uninstall/Repair */}
      <NotesModal
        open={notesModalOpen}
        title={modalAction === "uninstall" ? "Uninstall Part" : "Repair Part"}
        confirmLabel={modalAction === "uninstall" ? "Uninstall" : "Repair"}
        confirmColor={modalAction === "uninstall" ? "error" : "warning"}
        partId={actionRow?.L2PartId ?? null}
        action={modalAction ?? "uninstall"}
        onClose={handleCloseNotesModal}
        onConfirm={handleNotesModalConfirm}
        validateAgainstInitialDatetime
      />

      {/* New Spare Part Modal */}
      <NewSparePartModal
        open={newSparePartModalOpen}
        containerName={actionRow?.ContainerName ?? ""}
        onClose={handleCloseNewSparePartModal}
        onConfirm={handleNewSparePartConfirm}
        showMakeModelVendor={false}
        showSerialNumber={
          actionRow?.ContainerId === PART_IDS.COPPER_MOLD_TUBE_SPARES
        }
        showWarehouseDrawing
      />

      {/* Edit Part Modal */}
      <EditPartModal
        open={editPartModalOpen}
        selectedRow={actionRow}
        onClose={handleCloseEditPartModal}
        onConfirm={handleEditPartConfirm}
        showMakeModelVendor={false}
        showSerialNumber={
          actionRow?.PartTypeId === PART_TYPE_IDS.COPPER_MOLD_TUBE
        }
        showWarehouseDrawing
      />

      {/* Set Initial Metric Modal */}
      <SetInitialMetricModal
        open={setInitialMetricModalOpen}
        partName={
          actionRow?.L2PartName ??
          actionRow?.PositionName ??
          actionRow?.EquipmentName ??
          null
        }
        currentPartialValue={actionRow?.InitialAbsoluteValue ?? 0}
        showApplyToChildren={actionRow?.HasChildren === true}
        forceApplyToChildren={
          actionRow?.HasChildren === true && actionRow?.IsReplaceable === false
        }
        isLoading={setInitialMetricMutation.isPending}
        onClose={handleCloseSetInitialMetricModal}
        onConfirm={handleSetInitialMetricConfirm}
      />

      {/* Correct Dates Modal */}
      <CorrectDatesModal
        open={correctDatesModalOpen}
        partId={correctDatesPartId}
        partName={correctDatesPartName}
        onClose={handleCloseCorrectDatesModal}
        onSave={handleCorrectDatesSave}
        validateAgainstInitialDatetime
      />

      {/* Set Mold Online Modal (strand-based, dual mode) */}
      {actionRow &&
        moldOnlineModalOpen &&
        (moldOnlineFromStrand ? (
          <SetMoldOnlineModal
            open={moldOnlineModalOpen}
            mode="fromStrand"
            strandName={getStrandName(
              treeDataRef.current,
              actionRow.ContainerId ?? 0,
            )}
            strandPartId={actionRow.ContainerId ?? 0}
            availableMolds={availableSpareMolds}
            locationLastTimestamp={strandLocationTimestamp}
            occupantPartId={
              treeDataRef.current.find(
                (row) =>
                  row.rowType === "equipment" &&
                  row.StrandPartId === actionRow.ContainerId &&
                  row.Status === "Online",
              )?.EquipmentId ?? null
            }
            isLoading={
              setOnlineMutation.isPending || setOfflineMutation.isPending
            }
            onClose={handleCloseMoldOnlineModal}
            onConfirm={handleSetMoldOnlineConfirm}
          />
        ) : (
          <SetMoldOnlineModal
            open={moldOnlineModalOpen}
            mode="fromSpares"
            moldName={
              actionRow.EquipmentName ??
              actionRow.L2PartName ??
              `Mold #${actionRow.EquipmentId}`
            }
            moldPartId={actionRow.EquipmentId}
            availableStrands={availableStrands}
            occupantPartId={null}
            isLoading={
              setOnlineMutation.isPending || setOfflineMutation.isPending
            }
            onClose={handleCloseMoldOnlineModal}
            onConfirm={handleSetMoldOnlineConfirm}
          />
        ))}

      {/* Set Mold Offline Confirmation Modal */}
      <SetEquipmentStatusModal
        open={statusModalOpen}
        equipmentName={actionRow?.EquipmentName ?? null}
        equipmentType="Mold"
        action="offline"
        partId={actionRow?.EquipmentId ?? null}
        currentOnlineEquipmentName={null}
        occupantPartId={null}
        isLoading={setOfflineMutation.isPending}
        onClose={handleCloseStatusModal}
        onConfirm={handleStatusModalConfirm}
        validateAgainstInitialDatetime
      />

      {/* Delete Spare Part Confirmation Modal */}
      <DeleteConfirmModal
        open={deleteModalOpen}
        title="Delete Spare Part"
        message={`Are you sure you want to delete "${actionRow?.L2PartName ?? "this spare part"}"? This action cannot be undone.`}
        isLoading={deleteSpareMutation.isPending}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteSparePartConfirm}
      />

      {/* No Permission Modal */}
      <NoPermissionModal
        open={noPermissionModalOpen}
        actionName={noPermissionAction}
        onClose={() => setNoPermissionModalOpen(false)}
      />

      {/* Error Modal */}
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
    </>
  );
};
